import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import theme, { textBold } from 'theme/theme';
import MainTitle from 'base/components/MainTitle';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import SubmitButton from 'base/components/forms/SubmitButton';
import SpinnerOverlay from 'base/components/SpinnerOverlay';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import Button from 'base/components/Button';
import NavLink from 'base/components/NavLink';
import SplashScreen from 'auth/components/SplashScreen';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMatch } from 'react-router';
import usePrevious from 'hooks/usePrevious';
import {
  getResetPasswordHasError,
  getResetPasswordIsLoading,
  getResetPasswordIsSuccess,
  getVerifyTokenIsLoading,
} from '../../store/auth/selectors';
import { resetPassword, verifyResetToken } from '../../store/auth/actions';

const ErrorMessage = styled.div`
  margin: 0 0 2rem 0;
  ${textBold};
  color: ${theme.colors.primary};
`;

const PasswordResetForm = (
  { errors, isSubmitting }, // eslint-disable-line
) => (
  <Form>
    <FieldWrapper>Please enter a new password below</FieldWrapper>
    <Field type="password" name="password" placeholder="Password" component={FormikInputField} />
    <SubmitButton type="submit" disabled={isSubmitting}>
      Save password
    </SubmitButton>
  </Form>
);

export default function ResetPassword() {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getResetPasswordIsLoading);
  const isSuccess = useAppSelector(getResetPasswordIsSuccess);
  const hasError = useAppSelector(getResetPasswordHasError);
  const isVerifyLoading = useAppSelector(getVerifyTokenIsLoading);

  const match = useMatch('/reset-password/:token');
  const token = match?.params.token || '';
  const prevToken = usePrevious(match?.params.token);

  const verifyToken = useCallback(() => {
    if (token) {
      dispatch(verifyResetToken(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);
  useEffect(() => {
    if (prevToken !== token) {
      verifyToken();
    }
  }, [prevToken, token, verifyToken]);

  const resetSuccessContent = (
    <>
      <MainTitle withSpaceBelow>Success</MainTitle>
      Your password has been reset successfully.
      <NavLink to="/login">
        <Button>Go to login</Button>
      </NavLink>
    </>
  );

  const resetForm = isLoading ? (
    <SpinnerOverlay label="Submitting" />
  ) : (
    <Formik
      initialValues={{
        password: '',
      }}
      validationSchema={Yup.object().shape({
        password: Yup.string().required('Password is required.').min(6, 'Minimum 6 characters required'),
      })}
      render={PasswordResetForm}
      onSubmit={values => {
        dispatch(
          resetPassword({
            token,
            password: values.password,
          }),
        );
      }}
    />
  );

  const content = isVerifyLoading ? <SpinnerOverlay label="Verifying token" /> : resetForm;

  const mainContent = (
    <>
      <MainTitle withSpaceBelow>Reset password</MainTitle>
      {hasError ? (
        <>
          <ErrorMessage>Invalid or expired token</ErrorMessage>
          <div>
            This link can only be used once. Please continue to the <NavLink to="/login">login page</NavLink> or the{' '}
            <NavLink to="/forgot-password">forgot password page</NavLink>
          </div>
        </>
      ) : (
        content
      )}
    </>
  );

  return <SplashScreen>{isSuccess ? resetSuccessContent : mainContent}</SplashScreen>;
}
